import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Session } from "@supabase/supabase-js";
import { useRef } from "react";

interface CalendarError extends Error {
  status?: number;
}

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time?: string;
  impact?: 'low' | 'medium' | 'high';
  country?: string;
  currency?: string;
  actual?: string | number;
  forecast?: string | number;
  previous?: string | number;
}

export interface CalendarEventData {
  month: Record<string, {
    events: CalendarEvent[];
    display_score?: number;
    threshold?: number;
    total_score?: number;
    daily_sum?: number;
    volatility?: string;
  }>;
}

/**
 * Fetches calendar events from the API
 * 
 * Note about query limits:
 * This function respects the VITE_DISABLE_QUERY_LIMITS environment variable:
 * - When true: Proceeds with fetch even without an active session (development mode)
 * - When false or undefined: Requires an active session (production behavior)
 */
const fetchCalendarEvents = async (
  date: Date, 
  countries?: string[], 
  session?: Session | null,
  disableQueryLimits?: boolean
): Promise<CalendarEventData> => {
  console.log('useCalendarEvents: Starting fetch', { 
    date: format(date, 'yyyy-MM-dd'),
    countries,
    hasSession: !!session,
    userId: session?.user?.id
  });

  // Check if we should bypass the session requirement (for development)
  // This allows viewing calendar data without being logged in when the env var is set
  if (!session?.user?.id && !disableQueryLimits) {
    console.log('useCalendarEvents: No session available and query limits enabled, skipping fetch');
    throw new Error('No session available');
  }
  
  // When query limits are disabled, we can continue without a session
  if (!session?.user?.id && disableQueryLimits) {
    console.log('useCalendarEvents: No session but query limits are disabled, proceeding with fetch');
  }

  const month = format(date, 'MMM').toLowerCase();
  const year = format(date, 'yyyy');
  
  try {
    console.log('useCalendarEvents: Calling calendar function with params:', {
      month,
      year,
      countries: countries?.join(',')
    });

    const { data, error } = await supabase.functions.invoke<CalendarEventData>('calendar', {
      body: {
        month,
        year,
        ...(countries?.length ? { countries: countries.join(',') } : {})
      }
    });

    if (error) {
      console.error('useCalendarEvents: Edge function error:', error);
      throw error;
    }

    if (!data || !data.month) {
      console.error('useCalendarEvents: No data returned from edge function');
      throw new Error('No data returned from calendar API');
    }

    console.log('useCalendarEvents: Data fetched successfully', {
      monthKeys: Object.keys(data.month).length,
      hasEvents: Object.values(data.month).some(day => day.events?.length > 0)
    });

    return data;
  } catch (error) {
    console.error('useCalendarEvents: Error in fetchCalendarEvents:', error);
    
    const castedError = error as CalendarError;
    
    toast({
      title: "Error loading calendar events",
      description: castedError.message || "Failed to load calendar events. Please try again.",
      variant: "destructive",
    });
    
    throw castedError;
  }
};

export const useCalendarEvents = (
  date: Date, 
  countries?: string[], 
  session?: Session | null,
  options?: {
    staleTime?: number;
    gcTime?: number;
    retry?: boolean | number;
    enabled?: boolean;
  }
) => {
  /**
   * Check if query limits should be disabled (development mode)
   * When set to 'true', calendar data will be accessible without login
   * This makes development and testing easier
   */
  const disableQueryLimits = import.meta.env.VITE_DISABLE_QUERY_LIMITS === 'true';
  
  // Only log this once per component mount, not on every render
  const loggedRef = useRef(false);
  if (!loggedRef.current) {
    console.log('useCalendarEvents: Query limits disabled:', disableQueryLimits);
    loggedRef.current = true;
  }

  return useQuery({
    queryKey: ['calendar-events', format(date, 'yyyy-MM'), countries?.join(','), session?.user?.id, disableQueryLimits],
    queryFn: () => fetchCalendarEvents(date, countries, session, disableQueryLimits),
    enabled: options?.enabled ?? !!session?.user?.id,
    staleTime: options?.staleTime ?? 1000 * 60 * 5,
    gcTime: options?.gcTime ?? 1000 * 60 * 10,
    retry: options?.retry ?? 3,
  });
};